import React, { useState, useEffect, useRef, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, message } from 'antd';
import enUS from 'antd/locale/en_US';

// Public Pages
import Homepage from './PublicPages/NewHome.js';
import AboutUs from './PublicPages/AboutUs';
import ProductDetailPage from './PublicPages/ProductDetailPage';
import Login from './PublicPages/Login';
import Register from './PublicPages/Register';
import RegisterComingSoon from './PublicPages/RegisterComingSoon';
import PublicProductView from './PublicPages/PublicProductView';

// Admin Pages
import AdminLayout from './AdminPages/AdminLayout';
import AdminDashboard from './AdminPages/AdminDashboard';
import ProductManagement from './AdminPages/ProductManagement';
import EditProduct from './AdminPages/EditProduct';
import CategoryManagement from './AdminPages/CategoryManagement';
import CollectionManagement from './AdminPages/CollectionManagement';
import UserManagement from './AdminPages/UserManagement';
import AdminOrderManagement from './AdminPages/OrderManagement';
import AdminEditOrder from './AdminPages/EditOrder';
import CustomizationManagement from './AdminPages/CustomizationManagement';
import SupportTicketManagement from './AdminPages/SupportTicketManagement';
import FreightManagement from './AdminPages/FreightManagement';
import ExchangeRateManagement from './AdminPages/ExchangeRateManagement';
import SpecialPrice from './AdminPages/SpecialPrice';
import FinancialManagement from './AdminPages/FinancialManagement';
import ShippingCompanyManagement from './AdminPages/ShippingCompanyManagement';

// Dropshipper Pages
import DropshipperLayout from './DropshipperPages/DropshipperLayout';
import DropshipperDashboard from './DropshipperPages/DropshipperDashboard';
import DropshipperProductManagement from './DropshipperPages/DropshipperProductManagement';
import AddToMyStore from './DropshipperPages/AddToMyStore';
import DropshipperOrderManagement from './DropshipperPages/OrderManagement';
import DropshipperOrderDetails from './DropshipperPages/OrderDetails';
import DropshipperEditOrder from './DropshipperPages/EditOrder';
import MyCustomizations from './DropshipperPages/MyCustomizations';
import MySupportTickets from './DropshipperPages/MySupportTickets';
import ProductsList from './DropshipperPages/ProductsList';
import ViewFreight from './DropshipperPages/ViewFreight';
import MyStores from './DropshipperPages/MyStores';
import ConnectShopify from './DropshipperPages/ConnectShopify';
import ManualProductConnect from './DropshipperPages/ManualProductConnect';
import MyProfile from './DropshipperPages/MyProfile';
import MyDiscounts from './DropshipperPages/MyDiscounts';
import MyWallet from './DropshipperPages/MyWallet';
import UnmappedOrders from './DropshipperPages/UnmappedOrders';
import ImportThirdPartyStoreOrder from './DropshipperPages/ImportThirdPartyStoreOrder';

import { authAPI } from './api';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);

  // Admin state
  const [adminSelectedKey, setAdminSelectedKey] = useState('dashboard');
  
  // Dropshipper state
  const [dropshipperSelectedKey, setDropshipperSelectedKey] = useState('ch-products');

  const refreshTimerRef = useRef(null);

  const clearRefreshTimer = useCallback(() => {
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(() => {
    clearRefreshTimer();
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUserInfo(null);
    setAdminSelectedKey('dashboard');
    setDropshipperSelectedKey('ch-products');
  }, [clearRefreshTimer]);

  const refreshAccessToken = useCallback(async () => {
    const existingToken = localStorage.getItem('token');
    if (!existingToken) {
      return;
    }
    try {
      const response = await authAPI.refresh();
      const { access_token: accessToken } = response.data || {};
      if (accessToken) {
        localStorage.setItem('token', accessToken);
      }
    } catch (error) {
      message.warning('Session expired, please login again.');
      handleLogout();
    }
  }, [handleLogout]);

  const startRefreshTimer = useCallback(() => {
    clearRefreshTimer();
    refreshTimerRef.current = setInterval(() => {
      refreshAccessToken();
    }, 5 * 60 * 1000);
  }, [clearRefreshTimer, refreshAccessToken]);

  // Scroll to top on route change
  function ScrollToTop() {
    const { pathname } = useLocation();

    useEffect(() => {
      window.scrollTo(0, 0);
    }, [pathname]);

    return null;
  }

  useEffect(() => {
    // 检查用户是否已登录
    const token = localStorage.getItem('token');
    if (token) {
      fetchUserInfo();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      startRefreshTimer();
    } else {
      clearRefreshTimer();
    }
    return () => {
      clearRefreshTimer();
    };
  }, [isAuthenticated, startRefreshTimer, clearRefreshTimer]);

  const fetchUserInfo = async () => {
    try {
      const response = await authAPI.getProfile();
      setUserInfo(response.data);
      setIsAuthenticated(true);
    } catch (error) {
      localStorage.removeItem('token');
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (token) => {
    try {
      await fetchUserInfo();
      message.success('Login successful');
      return true; // 表示登录成功
    } catch (error) {
      message.error('Failed to get user information');
      return false; // 表示登录失败
    }
  };

  if (loading) {
    return <div style={{ height: '100vh', width: '100vw' }}></div>;
  }

  return (
    <ConfigProvider locale={enUS}>
      <div className="App">
        <Router>
          <ScrollToTop />
          <Routes>
            {/* 公开路由 */}
            <Route path="/" element={<Homepage />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/products/*" element={<PublicProductView />} />
            <Route path="/product/:product_id" element={<ProductDetailPage />} />
            <Route path="/login" element={<Login onLogin={handleLogin} />} />
            <Route path="/register" element={<Register />} />
            {/* <Route path="/register" element={<RegisterComingSoon />} /> */}
            
            {/* Admin后台路由 */}
            <Route 
              path="/admin/*" 
              element={
                isAuthenticated && userInfo?.role === 'admin' ? (
                  <Routes>
                    <Route path="/" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey={adminSelectedKey}
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <AdminDashboard />
                      </AdminLayout>
                    } />
                    <Route path="/dashboard" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="dashboard"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <AdminDashboard />
                      </AdminLayout>
                    } />
                    <Route path="/products" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="products"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <ProductManagement />
                      </AdminLayout>
                    } />
                    <Route path="/products/create" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="products"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <EditProduct />
                      </AdminLayout>
                    } />
                    <Route path="/products/edit/:product_id" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="products"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <EditProduct />
                      </AdminLayout>
                    } />
                    <Route path="/categories" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="categories"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <CategoryManagement />
                      </AdminLayout>
                    } />
                    <Route path="/collections" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="collections"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <CollectionManagement />
                      </AdminLayout>
                    } />
                    <Route path="/customizations" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="customizations"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <CustomizationManagement />
                      </AdminLayout>
                    } />
                    <Route path="/support-tickets" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="support-tickets"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <SupportTicketManagement />
                      </AdminLayout>
                    } />
                    <Route path="/admin/support-tickets" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="support-tickets"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <SupportTicketManagement />
                      </AdminLayout>
                    } />
                    <Route path="/orders" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <AdminOrderManagement />
                      </AdminLayout>
                    } />
                    <Route path="/orders/create" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <AdminEditOrder />
                      </AdminLayout>
                    } />
                    <Route path="/orders/edit/:orderNumber" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <AdminEditOrder />
                      </AdminLayout>
                    } />
                    <Route path="/freight" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="freight"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <FreightManagement />
                      </AdminLayout>
                    } />
                    <Route path="/shipping-companies" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="shipping-companies"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <ShippingCompanyManagement />
                      </AdminLayout>
                    } />
                    <Route path="/special-prices" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="special-prices"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <SpecialPrice />
                      </AdminLayout>
                    } />
                    <Route path="/exchange-rates" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="exchange-rates"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <ExchangeRateManagement />
                      </AdminLayout>
                    } />
                    <Route path="/financial" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="financial"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <FinancialManagement />
                      </AdminLayout>
                    } />
                    <Route path="/users" element={
                      <AdminLayout
                        onLogout={handleLogout}
                        selectedKey="users"
                        onMenuSelect={setAdminSelectedKey}
                        userInfo={userInfo}
                      >
                        <UserManagement />
                      </AdminLayout>
                    } />
                  </Routes>
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />
            
            {/* Shopify安装页面在未登录状态下也需要可访问 */}
            <Route 
              path="/dropshipper/connect-shopify" 
              element={
                isAuthenticated && userInfo?.role === 'dropshipper' ? (
                  <DropshipperLayout
                    onLogout={handleLogout}
                    selectedKey="my-stores"
                    onMenuSelect={setDropshipperSelectedKey}
                    userInfo={userInfo}
                  >
                    <ConnectShopify />
                  </DropshipperLayout>
                ) : (
                  <ConnectShopify />
                )
              } 
            />
            
            {/* Dropshipper后台路由 */}
            <Route 
              path="/dropshipper/*" 
              element={
                isAuthenticated && userInfo?.role === 'dropshipper' ? (
                  <Routes>
                    <Route path="/" element={<Navigate to="/dropshipper/ch-products" replace />} />
                    {/* <Route path="/dashboard" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="dashboard"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <DropshipperDashboard />
                      </DropshipperLayout>
                    } /> */}
                    <Route path="/ch-products/*" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="ch-products"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <ProductsList />
                      </DropshipperLayout>
                    } />
                    <Route path="/my-products" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-products"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <DropshipperProductManagement />
                      </DropshipperLayout>
                    } />
                    <Route path="/add-to-store/:product_id" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-products"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <AddToMyStore />
                      </DropshipperLayout>
                    } />
                    <Route path="/my-customizations" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-customizations"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MyCustomizations />
                      </DropshipperLayout>
                    } />
                    <Route path="/support" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="support"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MySupportTickets />
                      </DropshipperLayout>
                    } />
                    <Route path="/dropshipper/support" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="support"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MySupportTickets />
                      </DropshipperLayout>
                    } />
                    <Route path="/freight" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="freight"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <ViewFreight />
                      </DropshipperLayout>
                    } />
                    <Route path="/orders" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <DropshipperOrderManagement />
                      </DropshipperLayout>
                    } />
                    <Route path="/unmapped-orders" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="unmapped-orders"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <UnmappedOrders />
                      </DropshipperLayout>
                    } />
                    <Route path="/orders/:orderNumber" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <DropshipperOrderDetails />
                      </DropshipperLayout>
                    } />
                    <Route path="/create-order" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <DropshipperEditOrder />
                      </DropshipperLayout>
                    } />
                    <Route path="/edit-order/:orderNumber" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="orders"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <DropshipperEditOrder />
                      </DropshipperLayout>
                    } />
                    <Route path="/my-stores" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-stores"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MyStores />
                      </DropshipperLayout>
                    } />
                    <Route path="/import-third-party-store-order" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-stores"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <ImportThirdPartyStoreOrder />
                      </DropshipperLayout>
                    } />
                    <Route path="/manual-product-connect" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="manual-connect"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <ManualProductConnect />
                      </DropshipperLayout>
                    } />
                    <Route path="/my-discounts" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-discounts"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MyDiscounts />
                      </DropshipperLayout>
                    } />
                    <Route path="/my-wallet" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="my-wallet"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MyWallet />
                      </DropshipperLayout>
                    } />
                    <Route path="/profile" element={
                      <DropshipperLayout
                        onLogout={handleLogout}
                        selectedKey="profile"
                        onMenuSelect={setDropshipperSelectedKey}
                        userInfo={userInfo}
                      >
                        <MyProfile />
                      </DropshipperLayout>
                    } />
                  </Routes>
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />
            
            {/* 根据用户角色重定向 */}
            <Route 
              path="/dashboard" 
              element={
                isAuthenticated ? (
                  userInfo?.role === 'admin' ? (
                    <Navigate to="/admin" replace />
                  ) : userInfo?.role === 'dropshipper' ? (
                    <Navigate to="/dropshipper" replace />
                  ) : (
                    <Navigate to="/login" replace />
                  )
                ) : (
                  <Navigate to="/login" replace />
                )
              } 
            />
            
            {/* 默认重定向 */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </div>
    </ConfigProvider>
  );
}

export default App;
